import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { ErrorMessage, StatusChip } from "@msys/ui";
import { Check as CheckIcon } from "@mui/icons-material";
import { Box, Chip, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { enqueueSnackbar } from "notistack";
import React from "react";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../../commons/hooks/useCollectionView.js";
import { PAGE_LIST_RESULTS_PER_PAGE } from "../../../constants.js";
import { useDataGridStateStore } from "../../users/useDataGridStateStore.js";
import {
  productFiltersDefaultValue,
  ProductFiltersFormValues,
} from "../filters/ProductFilterFields.js";
import { useProductSearchType } from "../filters/useProductSearchTypes.js";
import { ProductSearchListHeader } from "../ProductSearchListHeader.js";
import { ProductSearchItem__ProductSearchResultFragment } from "../Product.generated.js";
import {
  productTypeRefOf,
  summariseAddProductPreferenceResults,
} from "../productPreferences.js";
import { useProductTypeAssignmentSearch_ProductsQuery } from "../ProductTypeAssignmentSearch.generated.js";
import { PimProductTypeSystemKey } from "../../../../clients/graphqlTypes.js";
import {
  evictRankedProductSearches,
  UseProductPreferences,
} from "../useProductPreferences.js";
import { ProductsListView } from "./ProductsListView.js";

interface Props {
  productTypeKey: string;
  /** The system the key belongs to; the reference system when omitted. */
  productTypeSystemKey?: PimProductTypeSystemKey;
  productTypeLabel: string;
  preferences: UseProductPreferences;
}

const ALLOWED_VIEWS: CollectionView[] = ["gallery", "list", "table"];

const keepSearchesAsTheyAre = () => undefined;

/**
 * Assigning articles from the catalogue to one product type.
 *
 * This is the inverse of the catalogue row control: there the product type comes from the
 * filter and the user marks preferred articles among the matches; here the product type is
 * fixed and the product search starts **unfiltered**, because the articles the catalogue has
 * not classified as that type are the ones a preference makes reachable. The user may narrow
 * it to the chosen type alone — never to another, since only the chosen type can be assigned.
 */
export function ProductTypeAssignmentSearch({
  productTypeKey,
  productTypeSystemKey,
  productTypeLabel,
  preferences,
}: Props) {
  const { t } = useTranslate(["Product", "Global"]);
  const client = useApolloClient();
  const stateStore = useDataGridStateStore("ProductTypeAssignment");

  const { productSearchTypeOptions, productSearchType, setProductSearchType } =
    useProductSearchType();

  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [filters, setFilters] = React.useState<ProductFiltersFormValues>(
    productFiltersDefaultValue
  );
  const [searchEnabled, setSearchEnabled] = React.useState<boolean>(false);
  const [onlyThisProductType, setOnlyThisProductType] =
    React.useState<boolean>(false);
  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>(
      "product-type-assignment",
      "list",
      "list"
    );
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: PAGE_LIST_RESULTS_PER_PAGE,
  });

  const productSearchVariables = React.useMemo(
    () => ({
      searchTerm:
        productSearchType === "fulltextSearch"
          ? searchTerm || undefined
          : undefined,
      specificFieldQueryFields:
        productSearchType !== "fulltextSearch" && searchTerm
          ? [{ field: productSearchType, value: searchTerm }]
          : undefined,
      filters: onlyThisProductType
        ? { ...filters, productTypeKeys: [productTypeKey] }
        : filters,
    }),
    [
      filters,
      productSearchType,
      searchTerm,
      onlyThisProductType,
      productTypeKey,
    ]
  );

  // A narrowed search has a different first page, so the paging starts over with it.
  React.useEffect(() => {
    setPaginationModel(current => ({ ...current, page: 0 }));
  }, [productSearchVariables]);

  // Unfiltered by product type unless narrowed to the chosen one — see the component
  // docblock. Every other filter the catalogue search offers is available, because narrowing
  // by supplier or brand is how the candidates for one product type are found at all.
  const query = useProductTypeAssignmentSearch_ProductsQuery({
    client,
    variables: {
      ...productSearchVariables,
      limit: paginationModel.pageSize,
      offset: paginationModel.pageSize * paginationModel.page,
    },
  });

  const connection = getDataOrNull(
    (query.data ?? query.previousData)?.pimSearchProducts
  );
  const items = connection?.edges.map(edge => edge.node) ?? [];
  const totalCount = connection?.totalCount ?? 0;

  // The already-assigned state comes from the stored preferences, not from what this session
  // happened to click: an article assigned last week must read as assigned on first render,
  // or the user redoes work they already did.
  const {
    preferredStateKnown,
    getPreferredScopes,
    addProductPreferences,
    deleteProductPreferences,
    writing,
  } = preferences;

  // An assignment must not reorder the results under the user: a write here keeps every cached
  // search as it is, and the ranked searches are evicted once the user leaves, so the
  // catalogue and placeholder searches pick up the new ranking then.
  const wroteRef = React.useRef(false);
  React.useEffect(
    () => () => {
      if (wroteRef.current) evictRankedProductSearches(client.cache);
    },
    [client]
  );

  const isAssigned = React.useCallback(
    (product: ProductSearchItem__ProductSearchResultFragment) => {
      const scopes = getPreferredScopes(product);
      return scopes.user || scopes.organisation;
    },
    [getPreferredScopes]
  );

  /**
   * The row is a toggle: a click assigns an unassigned article and takes the assignment back
   * on an assigned one. Only the caller's own preference is toggled — an article preferred by
   * the organisation is not theirs to withdraw from an unqualified click, so that case is
   * reported instead of acted on.
   */
  const toggle = React.useCallback(
    async (product: ProductSearchItem__ProductSearchResultFragment) => {
      if (!preferredStateKnown || writing) return;

      const identifier = {
        supplierId: product.supplierId,
        articleNumber: product.articleNumber,
      };
      const scopes = getPreferredScopes(product);

      if (scopes.user) {
        try {
          wroteRef.current = true;
          await deleteProductPreferences({
            update: keepSearchesAsTheyAre,
            variables: {
              input: {
                scope: "USER",
                productType: productTypeRefOf(
                  productTypeKey,
                  productTypeSystemKey
                ),
                articleNumberIdentifiers: [identifier],
              },
            },
          });
        } catch (error) {
          if (error instanceof Error)
            enqueueSnackbar(error.message, { variant: "error" });
          return;
        }
        enqueueSnackbar(t("Preference removed.", { ns: "Product" }), {
          variant: "success",
        });
        return;
      }

      if (scopes.organisation) {
        enqueueSnackbar(
          t(
            "Preferred by your organisation — remove it from this product type's preferences instead.",
            { ns: "Product" }
          ),
          { variant: "warning" }
        );
        return;
      }

      // One click, fixed `USER` scope. The scope is not offered because a user-scope
      // preference is visible to nobody else, which is what makes a single unqualified click
      // safe here.
      let result;
      try {
        wroteRef.current = true;
        result = await addProductPreferences({
          update: keepSearchesAsTheyAre,
          variables: {
            input: {
              scope: "USER",
              productType: productTypeRefOf(
                productTypeKey,
                productTypeSystemKey
              ),
              articleNumberIdentifiers: [identifier],
            },
          },
        });
      } catch (error) {
        if (error instanceof Error)
          enqueueSnackbar(error.message, { variant: "error" });
        return;
      }

      const summary = summariseAddProductPreferenceResults(
        result.data?.pimAddProductPreferences.results ?? []
      );

      for (const conflict of summary.conflicts) {
        // Not an error in the user's action: this is the catalogue stating what it believes
        // the article is. Reported by name because that disagreement is the information.
        enqueueSnackbar(
          t("Already classified as {productType} — not assigned.", {
            ns: "Product",
            productType: conflict.productTypeName,
          }),
          { variant: "warning" }
        );
      }
      for (const unknown of summary.unknownSuppliers) {
        enqueueSnackbar(
          t(
            "Article {articleNumber} could not be preferred: its supplier is unknown.",
            {
              ns: "Product",
              articleNumber: unknown.articleNumber,
            }
          ),
          { variant: "error" }
        );
      }
      if (summary.added.length > 0) {
        enqueueSnackbar(
          t("Assigned to {productType}.", {
            ns: "Product",
            productType: productTypeLabel,
          }),
          { variant: "success" }
        );
      }
    },
    [
      addProductPreferences,
      deleteProductPreferences,
      preferredStateKnown,
      writing,
      getPreferredScopes,
      productTypeKey,
      productTypeSystemKey,
      productTypeLabel,
      t,
    ]
  );

  const ActionButton = React.useCallback(
    ({
      product,
    }: {
      product: ProductSearchItem__ProductSearchResultFragment;
    }) =>
      preferredStateKnown && isAssigned(product) ? (
        <StatusChip label={t("Assigned", { ns: "Product" })} color="success" />
      ) : null,
    [preferredStateKnown, isAssigned, t]
  );

  return (
    <ProductSearchListHeader
      filters={filters}
      setFilters={setFilters}
      resetFilters={() => setFilters(productFiltersDefaultValue)}
      productSearchTypeOptions={productSearchTypeOptions}
      productSearchType={productSearchType}
      setProductSearchType={setProductSearchType}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      activeView={activeView}
      setActiveView={setActiveView}
      productSearchVariables={productSearchVariables}
      // The product type is the screen's, not the search's: the only product type filter is
      // the chosen type itself, offered below.
      canChangeProductType={false}
      // No suggestion dropdown: a product picked there would be a second result list next to
      // the one the user is assigning from, and picking one leads nowhere here.
      disableProductSuggestions
      searchEnabled={searchEnabled}
      setSearchEnabled={setSearchEnabled}
      allowedViews={ALLOWED_VIEWS}
    >
      {({ SearchLine, FiltersLine, ChipsLine, SearchBackdrop }) => (
        <Stack
          direction="column"
          spacing={1}
          // Positioned so the search line, which goes absolute while focused, expands within
          // this column instead of over the directory beside it.
          sx={{
            position: "relative",
            minHeight: 0,
            flex: 1,
            overflow: "hidden",
          }}
        >
          <Box>{SearchLine}</Box>
          {SearchBackdrop}
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
          >
            {FiltersLine}
            <Chip
              size="small"
              label={t("Only {productType}", {
                ns: "Product",
                productType: productTypeLabel,
              })}
              icon={onlyThisProductType ? <CheckIcon /> : undefined}
              color={onlyThisProductType ? "primary" : "default"}
              variant={onlyThisProductType ? "filled" : "outlined"}
              aria-pressed={onlyThisProductType}
              onClick={() => setOnlyThisProductType(current => !current)}
            />
          </Stack>
          {ChipsLine}
          {query.error && <ErrorMessage message={query.error.message} />}
          <ProductsListView
            items={items}
            totalCount={totalCount}
            activeView={activeView}
            loading={query.loading}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            // No sort props: relevance is the order this screen wants, and it is what
            // lets the preference boost show at all.
            onClick={toggle}
            CardActionButton={ActionButton}
            TableActions={ActionButton}
            stateStore={stateStore}
            tableActionsWidth={90}
          />
        </Stack>
      )}
    </ProductSearchListHeader>
  );
}
